import React from "react";
import { formatDateDay } from "../../utils/formateDate";

const KycStatus = ({ kycDocData, status, setReVerify }) => {
  const statusMap = {
    0: "rejected",
    1: "approved",
    2: "pending",
  };

  const normalizedStatus =
    typeof status === "number" ? statusMap[status] || "approved" : status;

  const statusConfig = {
    approved: {
      headerBg: "#2c3e50",
      title: "KYC Verified",
      subtitle: "Your identity has been successfully confirmed.",
      statusTitle: "Verification Complete",
      statusDescription:
        "Your identity documents have been reviewed and approved. Your account is now fully verified with all features available.",
      statusBadge: "Approved",
      statusColor: "#28a745",
      icon: (
        <svg className="check-icon" viewBox="0 0 24 24">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      ),
      detailsIcon: (
        <svg className="info-icon" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <path d="m9,12 2,2 4-4" />
        </svg>
      ),
      showValidUntil: true,
    },
    pending: {
      headerBg: "#2c3e50",
      title: "KYC Under Review",
      subtitle: "Your identity verification is currently in progress.",
      statusTitle: "Review in Progress",
      statusDescription:
        "Your identity documents are being reviewed by our verification team. This usually takes 24–48 hours. We will notify you once it's complete.",
      statusBadge: "Under Review",
      statusColor: "#f39c12",
      icon: (
        <svg className="clock-icon" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12,6 12,12 16,14" />
        </svg>
      ),
      detailsIcon: (
        <svg className="info-icon" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
      ),
      showValidUntil: false,
    },
    rejected: {
      headerBg: "#2c3e50",
      title: "KYC Verification Failed",
      subtitle: "We couldn’t verify your identity.",
      statusTitle: "Verification Rejected",
      statusDescription:
        "We were unable to verify your identity using the submitted documents. Please check the requirements and resubmit your documents.",
      statusBadge: "Rejected",
      statusColor: "#e74c3c",
      icon: (
        <svg className="x-icon" viewBox="0 0 24 24">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      ),
      detailsIcon: (
        <svg className="alert-icon" viewBox="0 0 24 24">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      ),
      showValidUntil: false,
    },
  };

  const config = statusConfig[normalizedStatus] || statusConfig.approved;

  return (
    <div style={{ margin: "2rem 2rem" }}>
      <div className="kyc-status-card">
        <div
          className="card-header"
          style={{ backgroundColor: config.headerBg }}
        >
          <div className="verification-icon">{config.icon}</div>
          <h1 className="card-title">{config.title}</h1>
          <p className="card-subtitle">{config.subtitle}</p>
        </div>

        <div className="card-body">
          {normalizedStatus !== "rejected" && (
            <div className="status-message">
              <h2 className="status-title">{config.statusTitle}</h2>
              <p className="status-description">{config.statusDescription}</p>
            </div>
          )}

          <div className="verification-details">
            <h3 className="details-title">
              {config.detailsIcon}
              Verification Details
            </h3>

            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span
                className="status-badge"
                style={{
                  color: config.statusColor,
                  borderColor: config.statusColor,
                }}
              >
                <svg className="badge-icon" viewBox="0 0 24 24">
                  {normalizedStatus === "approved" && (
                    <path d="M20 6L9 17l-5-5" />
                  )}
                  {normalizedStatus === "pending" && (
                    <>
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12,6 12,12 16,14" />
                    </>
                  )}
                  {normalizedStatus === "rejected" && (
                    <>
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </>
                  )}
                </svg>
                {config.statusBadge}
              </span>
            </div>

            <div className="detail-item">
              <span className="detail-label">
                {normalizedStatus === "approved"
                  ? "Verification Date"
                  : "Submitted Date"}
              </span>
              <span className="detail-value">
                {normalizedStatus === "approved"
                  ? formatDateDay(kycDocData?.updatedAt)
                  : formatDateDay(kycDocData?.createdAt)}
              </span>
            </div>

            <div className="detail-item">
              <span className="detail-label">Document Type</span>
              <span className="detail-value">
                {kycDocData?.documentType || "N/A"}
              </span>
            </div>

            <div className="detail-item">
              <span className="detail-label">Applicant ID</span>
              <span className="detail-value">
                {kycDocData?.applicantId || "N/A"}
              </span>
            </div>

            <div className="detail-item">
              <span className="detail-label">Verification ID</span>
              <span className="detail-value">
                {kycDocData?.verificationId || "N/A"}
              </span>
            </div>

            {config.showValidUntil && (
              <div className="detail-item">
                <span className="detail-label">Valid Until</span>
                <span className="detail-value">May 29, 2026</span>
              </div>
            )}

            {normalizedStatus === "pending" && (
              <div className="detail-item">
                <span className="detail-label">Expected Completion</span>
                <span className="detail-value">Within 48 hours</span>
              </div>
            )}

            {normalizedStatus === "rejected" && (
              <>
                {kycDocData?.documentDeclineReason && (
                  <div className="detail-item">
                    <span className="detail-label">
                      Document Decline Reason
                    </span>
                    <span className="detail-value">
                      {kycDocData.documentDeclineReason}
                    </span>
                  </div>
                )}

                {kycDocData?.addressDeclineReason && (
                  <div className="detail-item">
                    <span className="detail-label">Address Decline Reason</span>
                    <span className="detail-value">
                      {kycDocData.addressDeclineReason}
                    </span>
                  </div>
                )}

                <div className="detail-item">
                  <span className="detail-label">
                    {kycDocData?.isReVerify
                      ? "Next Action"
                      : "Next Scheduled Verification"}
                  </span>
                  <span
                    className="detail-value"
                    style={{ color: config.statusColor }}
                  >
                    {kycDocData?.isReVerify
                      ? "Please resubmit your documents."
                      : `Your next verification attempt is scheduled for ${formatDateDay(
                          kycDocData?.nextAttemptDate
                        )}.`}
                  </span>
                </div>
              </>
            )}
          </div>
          {normalizedStatus === "rejected" && kycDocData?.isReVerify && (
            <p
              className="status-description"
              style={{
                color: config.statusColor,
                fontSize: ".7rem",
                textAlign: "center",
              }}
            >
              Note: You have only {kycDocData?.attempt} attempt remaining. After
              that, verification will be available again after 28 days.
            </p>
          )}

          {normalizedStatus === "rejected" && (
            <div className="action-section">
              {kycDocData?.isReVerify ? (
                <button
                  className="retry-button"
                  onClick={() => setReVerify(true)}
                >
                  <svg className="retry-icon" viewBox="0 0 24 24">
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                  </svg>
                  Submit New Documents
                </button>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "18px 28px",
                    backgroundColor: "#ffffff",
                    border: `1px solid ${config.statusColor}30`,
                    borderLeft: `4px solid ${config.statusColor}`,
                    borderRadius: "8px",
                    color: config.statusColor,
                    fontSize: "15px",
                    fontWeight: "500",
                    lineHeight: "1.4",
                    maxWidth: "380px",
                    margin: "0 auto",
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  {kycDocData?.daysLeft > 0
                    ? `${kycDocData.daysLeft} day${
                        kycDocData.daysLeft > 1 ? "s" : ""
                      } left for re-verification`
                    : "Next verification attempt is scheduled soon."}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KycStatus;
